import React, { useEffect, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Box,
  Grid,
  Typography,
} from "@material-ui/core";
import {
  FormControl as MuiFormControl,
  InputLabel as MuiInputLabel,
  MenuItem as MuiMenuItem,
  Select as MuiSelect,
} from "@mui/material";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ChatiaDialog from "../../components/ChatiaDialog";

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  const dsColors = theme.chatiaDS?.colors || {};

  return {
    content: {
      padding: "6px 2px 2px",
    },
    sectionTitle: {
      fontSize: 13,
      fontWeight: 700,
      marginBottom: 4,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
    },
    sectionHint: {
      fontSize: 12,
      lineHeight: 1.45,
      marginBottom: 18,
      color:
        dsColors.textSecondary ||
        (isDark ? "rgba(255,255,255,0.56)" : "rgba(0,0,0,0.56)"),
    },
  };
});

const initialSettings = {
  messageInterval: 20,
  longerIntervalAfter: 20,
  greaterInterval: 60,
};

const messageIntervalOptions = [0, 5, 10, 15, 20, 30, 40, 60, 80, 100, 120];
const longerIntervalOptions = [0, 5, 10, 15, 20, 30, 40, 50, 60];
const greaterIntervalOptions = [
  0,
  20,
  30,
  40,
  50,
  60,
  70,
  80,
  90,
  100,
  110,
  120,
  130,
  140,
  150,
  160,
  170,
  180,
];

const numericSettingKeys = [
  "messageInterval",
  "longerIntervalAfter",
  "greaterInterval",
];

const CampaignsConfig = ({ open, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [settings, setSettings] = useState(initialSettings);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    api
      .get("/campaign-settings")
      .then(({ data }) => {
        if (!mounted || !Array.isArray(data) || data.length === 0) return;
        const settingsList = data.map((item) => [
          item.key,
          numericSettingKeys.includes(item.key)
            ? Number(item.value)
            : item.value,
        ]);
        setSettings((prev) => ({
          ...prev,
          ...Object.fromEntries(settingsList),
        }));
      })
      .catch((err) => toastError(err));

    return () => {
      mounted = false;
    };
  }, [open]);

  const handleOnChangeSettings = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.post("/campaign-settings", { settings });
      toast.success(i18n.t("campaignsConfig.settingsSaved"));
      if (onClose) onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const isDark = theme.palette.type === "dark" || theme.mode === "dark";
  const dialogSurface = isDark ? "#121212" : "#ffffff";
  const scrollbarStyles = theme.scrollbarStyles || {};

  const reportFieldSx = {
    mt: "8px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px !important",
      minHeight: 40,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "12px !important",
    },
    "& .MuiInputBase-root": {
      borderRadius: "12px !important",
    },
    "& .MuiInputLabel-root": {
      backgroundColor: dialogSurface,
      px: "4px",
    },
  };

  const reportSelectMenuProps = {
    PaperProps: {
      sx: {
        mt: 1,
        borderRadius: "14px",
        boxShadow: "0 10px 36px rgba(0,0,0,0.14)",
        border: "none",
        backgroundImage: "none",
        overflow: "hidden",
        "& .MuiMenu-list": {
          padding: "6px",
          maxHeight: 220,
          overflowY: "auto",
          ...scrollbarStyles,
        },
        "& .MuiMenuItem-root": {
          borderRadius: "8px",
          padding: "9px 12px",
          marginBottom: "4px",
          fontSize: 14,
          fontWeight: 500,
          "&.Mui-selected": {
            background: "rgba(6, 207, 156, 0.10)",
          },
          "&.Mui-selected:hover": {
            background: "rgba(6, 207, 156, 0.16)",
          },
        },
      },
    },
  };

  const renderSecondsOption = (value) => (
    <MuiMenuItem key={value} value={value}>
      {value === 0
        ? i18n.t("campaigns.settings.noBreak")
        : `${value} ${i18n.t("campaignsConfig.seconds")}`}
    </MuiMenuItem>
  );

  const renderMessagesOption = (value) => (
    <MuiMenuItem key={value} value={value}>
      {value === 0
        ? i18n.t("campaigns.settings.undefined")
        : `${value} ${i18n.t("campaigns.settings.messages")}`}
    </MuiMenuItem>
  );

  return (
    <ChatiaDialog
      open={!!open}
      onClose={onClose}
      title={i18n.t("campaignsConfig.title")}
      maxWidth="md"
      onSave={saveSettings}
      saveText={i18n.t("campaigns.settings.save")}
      saveDisabled={saving}
      saveLoading={saving}
    >
      <Box className={classes.content}>
        <Typography className={classes.sectionTitle}>
          {i18n.t("campaignsConfig.intervals")}
        </Typography>
        <Typography className={classes.sectionHint}>
          {i18n.t("campaignsConfig.intervalDescription")}
        </Typography>

        <Grid spacing={2} container>
          <Grid xs={12} md={4} item>
            <MuiFormControl
              variant="outlined"
              fullWidth
              size="small"
              sx={reportFieldSx}
            >
              <MuiInputLabel id="messageInterval-label">
                {i18n.t("campaigns.settings.randomInterval")}
              </MuiInputLabel>
              <MuiSelect
                name="messageInterval"
                id="messageInterval"
                labelId="messageInterval-label"
                label={i18n.t("campaigns.settings.randomInterval")}
                value={settings.messageInterval}
                onChange={handleOnChangeSettings}
                MenuProps={reportSelectMenuProps}
              >
                {messageIntervalOptions.map(renderSecondsOption)}
              </MuiSelect>
            </MuiFormControl>
          </Grid>

          <Grid xs={12} md={4} item>
            <MuiFormControl
              variant="outlined"
              fullWidth
              size="small"
              sx={reportFieldSx}
            >
              <MuiInputLabel id="longerIntervalAfter-label">
                {i18n.t("campaigns.settings.intervalGapAfter")}
              </MuiInputLabel>
              <MuiSelect
                name="longerIntervalAfter"
                id="longerIntervalAfter"
                labelId="longerIntervalAfter-label"
                label={i18n.t("campaigns.settings.intervalGapAfter")}
                value={settings.longerIntervalAfter}
                onChange={handleOnChangeSettings}
                MenuProps={reportSelectMenuProps}
              >
                {longerIntervalOptions.map(renderMessagesOption)}
              </MuiSelect>
            </MuiFormControl>
          </Grid>

          <Grid xs={12} md={4} item>
            <MuiFormControl
              variant="outlined"
              fullWidth
              size="small"
              sx={reportFieldSx}
            >
              <MuiInputLabel id="greaterInterval-label">
                {i18n.t("campaigns.settings.laggerTriggerRange")}
              </MuiInputLabel>
              <MuiSelect
                name="greaterInterval"
                id="greaterInterval"
                labelId="greaterInterval-label"
                label={i18n.t("campaigns.settings.laggerTriggerRange")}
                value={settings.greaterInterval}
                onChange={handleOnChangeSettings}
                MenuProps={reportSelectMenuProps}
              >
                {greaterIntervalOptions.map(renderSecondsOption)}
              </MuiSelect>
            </MuiFormControl>
          </Grid>
        </Grid>
      </Box>
    </ChatiaDialog>
  );
};

export default CampaignsConfig;
